import { Component, h, Prop, Watch } from '@stencil/core';

interface UiCmsTableBody {
  tableHtml: string;
  style?:
    | 'colorTablePrimaryBlue'
    | 'colorTablePrimaryGreen'
    | 'colorTableGray'
    | 'colorTableTurquoise'
    | 'colorTableYellow'
    | 'colorTableLightBlue'
    | 'colorTableViolet'
    | 'colorTableYellowGreen'
    | 'colorTableHighlight'
    | 'colorTableTrafficLight'
    | 'colorTableRisk'
    | 'colorTableColorClimate'
    | 'colorTableIllustrations';
  firstColumnAsHeader: boolean;
  firstRowAsHeader: boolean;
  showVerticalBorders: boolean;
  background: 'none' | 'white' | 'alternating-blue' | 'alternating-gray' | 'headlines-blue' | 'headlines-gray'; // Note: 'none' is transparent
}

@Component({
  tag: 'ui-cms-table',
  styleUrl: 'ui-cms-table.scss',
  shadow: false,
})
export class UiCmsTable {
  @Prop({ mutable: true }) body: UiCmsTableBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
    this.constructTableHTML();
  }

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
  }

  private tableContent: HTMLTableElement | null = null;

  private constructTableHTML() {
    // Get table from body
    const temp = document.createElement('div');
    temp.innerHTML = this.body.tableHtml;
    const table = temp.querySelector('table');

    if (!table) {
      this.tableContent = null;
    }

    this.setSemanticallyCorrectHeaderRow(table);
    this.setSemanticallyCorrectTableColumnHeaders(table);
    this.assureNoThInBody(table);
    this.applyCustomStyling(table);

    temp.remove();
    this.tableContent = table;
  }

  private applyCustomStyling(table: HTMLTableElement) {
    // Style header row
    const headerRow = table.querySelector('thead tr');
    const headerCells = headerRow?.querySelectorAll('th');
    const headerStyles: Record<Exclude<UiCmsTableBody['background'], 'none'>, string> = {
      white: 'sd-table-cell--bg-white',
      'alternating-blue': 'sd-table-cell--bg-white',
      'alternating-gray': 'sd-table-cell--bg-white',
      'headlines-blue': 'sd-table-cell--bg-primary-100',
      'headlines-gray': 'sd-table-cell--bg-neutral-100',
    };
    headerCells?.forEach((cell, cellIndex) => {
      cell.classList.add('sd-table-cell');
      if (this.body.showVerticalBorders && cellIndex !== headerCells.length - 1) {
        cell.classList.add('sd-table-cell--divider');
      }
      if (this.body.background in headerStyles) {
        cell.classList.add(headerStyles[this.body.background]);
      }
    });

    // Style body rows
    const bodyRows = table?.querySelectorAll('tbody tr');
    bodyRows?.forEach((bodyRow, bodyRowIndex) => {
      const cells = bodyRow.querySelectorAll('td, th');
      cells?.forEach((cell, cellIndex) => {
        // Basic styling
        cell.classList.add('sd-table-cell');

        // Add vertical borders
        if (this.body.showVerticalBorders && cellIndex !== cells.length - 1) {
          cell.classList.add('sd-table-cell--divider');
        }

        // Add background color
        const shouldAddBackground = headerRow ? bodyRowIndex % 2 === 0 : bodyRowIndex % 2 === 1;
        const backgroundClasses: Record<
          Extract<UiCmsTableBody['background'], 'alternating-blue' | 'alternating-gray' | 'white'>,
          string
        > = {
          'alternating-blue': shouldAddBackground ? 'sd-table-cell--bg-primary-100' : 'sd-table-cell--bg-white',
          'alternating-gray': shouldAddBackground ? 'sd-table-cell--bg-neutral-100' : 'sd-table-cell--bg-white',
          white: 'sd-table-cell--bg-white',
        };

        if (this.body.background in backgroundClasses) {
          cell.classList.add(backgroundClasses[this.body.background]);
        }
      });

      // Add styling to row header
      const rowHeader = bodyRow.querySelector('th');
      if (rowHeader) {
        const backgroundClasses: Record<
          Extract<UiCmsTableBody['background'], 'headlines-blue' | 'headlines-gray'>,
          string
        > = {
          'headlines-blue': 'sd-table-cell--bg-primary-100',
          'headlines-gray': 'sd-table-cell--bg-neutral-100',
        };

        if (this.body.background in backgroundClasses) {
          rowHeader.classList.add(backgroundClasses[this.body.background]);
        }
      }
    });
  }

  /**
   * This makes sure that the header row is semantically correct.
   * The magnolia table creation does not handle this correctly.
   * This is important for the table to be accessible.
   */
  private setSemanticallyCorrectHeaderRow(table: HTMLTableElement) {
    const theadRow = table.querySelector('thead tr');

    if ((!theadRow && !this.body.firstRowAsHeader) || (!!theadRow && this.body.firstRowAsHeader)) {
      return; // Semantically correct
    }

    if (!!theadRow && !this.body.firstRowAsHeader) {
      // move first row to top of tbody and remove thead
      const tbody = table.querySelector('tbody');
      // change all ths to tds
      const cells = theadRow.querySelectorAll('th');
      cells?.forEach((cell) => {
        const td = document.createElement('td');
        td.innerHTML = cell.innerHTML;
        theadRow.replaceChild(td, cell);
      });
      tbody?.prepend(theadRow);

      const thead = table.querySelector('thead');
      thead?.remove();
    }

    if (!theadRow && this.body.firstRowAsHeader) {
      const rowToMove = table.querySelector('tbody tr');
      // change all tds to ths
      const cells = rowToMove.querySelectorAll('td');
      cells?.forEach((cell) => {
        const th = document.createElement('th');
        th.innerHTML = cell.innerHTML;
        th.setAttribute('scope', 'col');
        rowToMove.replaceChild(th, cell);
      });

      // create thead and move first row to it
      const thead = document.createElement('thead');
      thead.innerHTML = rowToMove.innerHTML;
      table.prepend(thead);
      rowToMove.remove();
    }
  }

  /**
   * Sometimes there are <th> in body rows which should not happen.
   * This is likely caused by the magnolia table creation.
   * This function removes all unwanted <th> in the body.
   */
  private assureNoThInBody(table: HTMLTableElement) {
    const tbodyRows = table.querySelectorAll('tbody tr');
    tbodyRows?.forEach((row) => {
      const ths = row.querySelectorAll('th');
      ths?.forEach((th, index) => {
        if (index === 0 && this.body.firstColumnAsHeader) {
          return; // Keep the first <th> if firstColumnAsHeader is true
        }
        const td = document.createElement('td');
        td.innerHTML = th.innerHTML;
        row.replaceChild(td, th);
      });
    });
  }

  private setSemanticallyCorrectTableColumnHeaders(table: HTMLTableElement) {
    if (!this.body.firstColumnAsHeader) {
      return;
    }

    // Change the first td of each row to th
    const tbodyRows = table.querySelectorAll('tbody tr');
    tbodyRows?.forEach((row) => {
      // skip if th exist in row
      if (row.querySelector('th')) {
        return;
      }

      const firstTd = row.querySelector('td');
      if (firstTd) {
        const th = document.createElement('th');
        th.innerHTML = firstTd.innerHTML;
        th.setAttribute('scope', 'row');
        row.replaceChild(th, firstTd);
      }
    });
  }

  render() {
    return (
      <table
        class={{
          'sd-table': true,
          firstColumnAsHeader: this.body.firstColumnAsHeader,
          firstRowAsHeader: this.body.firstRowAsHeader,
          [this.body.style]: !!this.body.style,
          'color-table': !!this.body.style,
        }}
        innerHTML={this.tableContent?.innerHTML}
      ></table>
    );
  }
}
